import { z } from 'zod';
/**
 * Kỳ báo cáo của dự án bảo trì — APM-MAINT-e.
 *
 * ## Vì sao dự án bảo trì cần kỳ, còn hai loại kia thì không
 *
 * Dự án Agile chia theo **sprint**, Waterfall chia theo **pha** — cả hai đều có
 * điểm kết thúc, nên "cả dự án" là một câu hỏi trả lời được. Dự án bảo trì thì
 * chạy tới khi PM dừng (APM-MAINT-b), nên sau ba năm "cả dự án" là một danh
 * sách hai nghìn việc không có điểm dừng, và mọi báo cáo trên đó gộp cả ba năm
 * vào một con số.
 *
 * Kỳ lịch (tháng / quý / năm) là cách chia tự nhiên của công việc vận hành: nó
 * khớp với chu kỳ báo cáo cho khách hàng và với chu kỳ ngân sách, và nó không
 * cần ai lập kế hoạch để tồn tại — khác sprint, thứ phải được tạo ra.
 *
 * ## Vì sao chuỗi chứ không phải cặp ngày
 *
 * Kỳ đi lên URL để chia sẻ được. `?period=2026-Q3` đọc ra ngay là quý mấy;
 * `?from=2026-07-01&to=2026-09-30` thì phải tự nhẩm, và hai người gõ tay hai
 * kiểu sẽ ra hai phạm vi lệch nhau vài ngày. Chuỗi cũng chuẩn hoá được: chỉ có
 * **một** cách viết quý 3 năm 2026.
 */
export const MAINTENANCE_PERIOD_RE = /^(\d{4})(?:-(?:Q([1-4])|(0[1-9]|1[0-2])))?$/;
/** `2026` (năm) · `2026-Q3` (quý) · `2026-08` (tháng). */
export const MaintenancePeriod = z.string().regex(MAINTENANCE_PERIOD_RE);
/**
 * Đổi một mã kỳ thành khoảng ngày.
 *
 * ## Vì sao `to` là ngày cuối kỳ, không phải ngày đầu kỳ sau
 *
 * Mọi bộ lọc trong ứng dụng so ngày lịch bằng khoảng **đóng** (`[start, due]`
 * của một task là khoảng đóng — xem `durationDays`). Trả về mốc mở ở đây nghĩa
 * là mỗi chỗ dùng phải nhớ trừ đi một ngày, và chỗ nào quên thì kỳ này nuốt
 * mất ngày đầu của kỳ sau — sai đúng một ngày, tức là gần như không ai thấy.
 *
 * @returns `null` khi mã kỳ không hợp lệ. Mã đến từ URL nên người dùng sửa được,
 * và một `null` để chỗ gọi rơi về mặc định thì dễ hiểu hơn một ngoại lệ.
 */
export function periodRange(period) {
    const match = MAINTENANCE_PERIOD_RE.exec(period);
    if (!match)
        return null;
    const year = Number(match[1]);
    const quarter = match[2] ? Number(match[2]) : null;
    const month = match[3] ? Number(match[3]) : null;
    if (quarter !== null) {
        const first = (quarter - 1) * 3 + 1;
        return {
            kind: 'quarter',
            from: day(year, first, 1),
            to: day(year, first + 2, lastDay(year, first + 2)),
        };
    }
    if (month !== null) {
        return { kind: 'month', from: day(year, month, 1), to: day(year, month, lastDay(year, month)) };
    }
    return { kind: 'year', from: day(year, 1, 1), to: day(year, 12, 31) };
}
/** Mã kỳ chứa một ngày, theo loại kỳ đang xem. */
export function periodOf(date, kind) {
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1;
    if (kind === 'year')
        return String(year);
    if (kind === 'quarter')
        return `${year}-Q${Math.floor((month - 1) / 3) + 1}`;
    return `${year}-${String(month).padStart(2, '0')}`;
}
/**
 * Kỳ liền trước / liền sau — cho hai nút mũi tên cạnh bộ chọn.
 *
 * Đi bằng ngày giữa kỳ chứ không cộng trừ số học trên mã: `2026-01` lùi một
 * tháng phải ra `2025-12`, và một phép trừ trên số tháng sẽ cho `2026-00`.
 */
export function shiftPeriod(period, step) {
    const range = periodRange(period);
    if (!range)
        return null;
    const [year, month] = [Number(range.from.slice(0, 4)), Number(range.from.slice(5, 7))];
    if (range.kind === 'year')
        return periodOf(utc(year + step, 1, 1), 'year');
    if (range.kind === 'quarter')
        return periodOf(utc(year, month + step * 3, 1), 'quarter');
    return periodOf(utc(year, month + step, 1), 'month');
}
/** `Date.UTC` tự cuộn tháng ngoài `1..12` sang năm liền kề — đúng điều cần ở đây. */
function utc(year, month, dayOfMonth) {
    return new Date(Date.UTC(year, month - 1, dayOfMonth));
}
function day(year, month, dayOfMonth) {
    return `${year}-${String(month).padStart(2, '0')}-${String(dayOfMonth).padStart(2, '0')}`;
}
/** Ngày 0 của tháng sau = ngày cuối tháng này. Tự đúng cả với tháng 2 năm nhuận. */
function lastDay(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
//# sourceMappingURL=period.js.map